import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit } from "@angular/core";
import { ColourUtilities } from "@common/lib/utilities/colour-utilities";

@Component({
    selector: "adapt-colour-swatch",
    templateUrl: "./colour-swatch.component.html",
    styleUrl: "./colour-swatch.component.css",
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColourSwatchComponent implements OnInit {
    @Input() public variable?: string;
    @Input() public textColor?: string;
    @Input() public textColorVariable?: string;
    @Input() public cssVariablePrefix = "adapt";
    @Input() public isPercentage = true;
    @Input() public variants?: number[];

    public colorMap: Map<string, string> = new Map<string, string>();
    public colorVariants: { name: string, value: string }[] = [];
    private rgbaCache: Map<string, Uint8ClampedArray> = new Map<string, Uint8ClampedArray>();

    public constructor(private elementRef: ElementRef) { }

    public ngOnInit() {
        this.colorMap = this.getStyleMap();
        this.colorVariants = this.getColorVariants();
    }

    public get textColorValue() {
        if (this.textColorVariable && this.colorMap.has(this.textColorVariable)) {
            return this.colorMap.get(this.textColorVariable);
        }

        return this.textColor ?? "#fff";
    }

    public get value() {
        return this.colorMap.get(this.variable!) ?? "#000";
    }

    public get rgb() {
        const rgba = this.getColorRGBA(this.value);
        return `rgb(${rgba[0]}, ${rgba[1]}, ${rgba[2]})`;
    }

    public get hex() {
        const rgba = this.getColorRGBA(this.value);
        const hex = [0, 1, 2].map(
            (idx) => ColourUtilities.byteToHex(rgba[idx]),
        ).join("");
        return `#${hex}`;
    }

    public getCssVariableDisplay(name?: string) {
        return `var(--${this.cssVariablePrefix}-${name})`;
    }

    private getStyleMap() {
        const variables = [];
        for (const stylesheet of Array.from(document.styleSheets)) {
            for (const rule of Array.from(stylesheet.cssRules) as CSSStyleRule[]) {
                if (this.isSelectorMatch(rule.selectorText)) {
                    const matchingVariables = Array.from(rule.style)
                        .filter((name) => name.startsWith(`--${this.cssVariablePrefix}-`));
                    variables.push(...matchingVariables);
                }
            }
        }

        return new Map<string, string>(variables.map((variable) => {
            const rawValue = ColourUtilities.getCssVariableValue(variable, this.elementRef.nativeElement);
            const rgba = this.getColorRGBA(rawValue);
            const hex = [0, 1, 2].map(
                (idx) => ColourUtilities.byteToHex(rgba[idx]),
            ).join("");

            return [
                variable.replace(`--${this.cssVariablePrefix}-`, ""),
                `#${hex}`,
            ];
        }));
    }

    private getColorRGBA(color: string): Uint8ClampedArray {
        // colorToRGBA uses a canvas and tanks performance so cache the results
        if (!this.rgbaCache.has(color)) {
            this.rgbaCache.set(color, ColourUtilities.colourToRGBA(color, this.elementRef.nativeElement));
        }
        return this.rgbaCache.get(color)!;
    }

    private isSelectorMatch(selectorText: string | null): boolean {
        if (!selectorText) {
            return false;
        }

        // root has the variables
        if (selectorText.startsWith(":root")) {
            return true;
        }

        // only the theme body has the variables set
        if (selectorText.includes("[data-bs-theme")) {
            try {
                return this.elementRef.nativeElement.closest(selectorText) !== null;
            } catch {
                return false;
            }
        }

        return false;
    }

    private getColorVariants() {
        return (this.variants ?? []).map((variant) => {
            const variantVariableName = `${this.variable!}-${variant}`;
            return { name: variant.toString(), value: this.colorMap.get(variantVariableName) ?? "#000" };
        });
    }
}
